import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { getThemes, createTheme, updateTheme, deleteTheme } from '@/api/themes';
import type { Theme } from '@/types/login-photo';
import { Plus, Edit, Trash2, Tag, Check, X } from 'lucide-react';

interface ThemeManagerProps {
  open: boolean;
  onClose: () => void;
  onUpdate?: () => void;
}

export function ThemeManager({ open, onClose, onUpdate }: ThemeManagerProps) {
  const [themes, setThemes] = useState<Theme[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newThemeName, setNewThemeName] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');
  const [creating, setCreating] = useState(false);

  const fetchThemes = async () => {
    setLoading(true);
    try {
      const data = await getThemes();
      setThemes(data);
    } catch (err) {
      setError('Erreur lors du chargement des themes');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchThemes();
    }
  }, [open]);

  const handleCreate = async () => {
    if (!newThemeName.trim()) return;

    setCreating(true);
    setError(null);
    try {
      await createTheme(newThemeName.trim());
      setNewThemeName('');
      fetchThemes();
      onUpdate?.();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Erreur lors de la creation');
    } finally {
      setCreating(false);
    }
  };

  const handleUpdate = async (id: number) => {
    if (!editingName.trim()) return;

    setError(null);
    try {
      await updateTheme(id, editingName.trim());
      setEditingId(null);
      setEditingName('');
      fetchThemes();
      onUpdate?.();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Erreur lors de la mise a jour');
    }
  };

  const handleDelete = async (id: number) => {
    const theme = themes.find((t) => t.id === id);
    if (!confirm(`Etes-vous sur de vouloir supprimer le theme "${theme?.name}" ?`)) {
      return;
    }

    setError(null);
    try {
      await deleteTheme(id);
      fetchThemes();
      onUpdate?.();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const startEditing = (theme: Theme) => {
    setEditingId(theme.id);
    setEditingName(theme.name);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingName('');
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5" />
            Gestion des themes
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Error */}
          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Add new theme */}
          <div className="flex gap-2">
            <Input
              placeholder="Nouveau theme..."
              value={newThemeName}
              onChange={(e) => setNewThemeName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreate();
              }}
            />
            <Button onClick={handleCreate} disabled={creating || !newThemeName.trim()}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>

          {/* Themes list */}
          <div className="border rounded-lg divide-y max-h-[300px] overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-muted-foreground text-sm">
                Chargement...
              </div>
            ) : themes.length === 0 ? (
              <div className="p-4 text-center text-muted-foreground text-sm">
                Aucun theme
              </div>
            ) : (
              themes.map((theme) => (
                <div
                  key={theme.id}
                  className="flex items-center gap-2 p-3 hover:bg-muted/50"
                >
                  {editingId === theme.id ? (
                    <>
                      <Input
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleUpdate(theme.id);
                          if (e.key === 'Escape') cancelEditing();
                        }}
                        className="flex-1 h-8"
                        autoFocus
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleUpdate(theme.id)}
                        className="h-8 w-8 p-0"
                      >
                        <Check className="h-4 w-4 text-green-600" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={cancelEditing}
                        className="h-8 w-8 p-0"
                      >
                        <X className="h-4 w-4 text-gray-500" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <div className="flex-1">
                        <div className="font-medium text-sm">{theme.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {theme.photoCount} photo{theme.photoCount !== 1 ? 's' : ''}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => startEditing(theme)}
                        className="h-8 w-8 p-0"
                      >
                        <Edit className="h-4 w-4 text-gray-500" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(theme.id)}
                        className="h-8 w-8 p-0"
                        disabled={theme.photoCount > 0}
                        title={
                          theme.photoCount > 0
                            ? 'Impossible de supprimer un theme utilise'
                            : 'Supprimer'
                        }
                      >
                        <Trash2
                          className={`h-4 w-4 ${
                            theme.photoCount > 0 ? 'text-gray-300' : 'text-red-500'
                          }`}
                        />
                      </Button>
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
